import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { committedQtyConfigForUnit, formatNumberWithCommas } from '@hero/core';
import type { BillingUnit } from '@hero/schema';
import { cn } from '@hero/web-lib';
import { PackageIcon, TruckIcon } from '@hero/web-icons';

export interface CommitmentStepperProps {
  value: number;
  onChange: (next: number) => void;
  /**
   * What the selected plan meters — decides the icon, the unit label,
   * and the default snap points. Defaults to `vehicle`.
   */
  unit?: BillingUnit;
  /**
   * Discrete snap stops the slider can land on. The last stop is
   * treated as a "plus" overflow: when the value lands there a
   * free-form input appears so the user can type 150, 200, etc.
   *
   * Defaults to the snap points for `unit` from `SUBSCRIPTION_CONFIG`.
   */
  snapPoints?: number[];
  /**
   * Snap values that get a label printed under the slider track.
   * Pick a sparse set; labeling every stop looks crowded.
   *
   * Default: every snap point (we keep the set short by design).
   */
  labeledSnapPoints?: number[];
  /**
   * Quick-pick chips shown above the slider. The last chip is
   * rendered as `"<n>+"` and toggles the custom input.
   *
   * Default: same as `snapPoints`.
   */
  quickPicks?: number[];
  /** Override the default localized title. */
  title?: string;
  /** Override the default localized description. */
  description?: string;
  className?: string;
}

/**
 * Committed-quantity snap-stop slider card — vehicle seats or monthly
 * order volume, depending on `unit`.
 *
 * Slider lands on values from `snapPoints` (defaults to the sizing block
 * for the unit). The last stop is the "plus" gateway — once you land
 * there the slider stays pinned to the end and a free-form number input
 * appears so you can type 150, 200, 500, etc. Quick-pick chips above the
 * track jump straight to a value.
 *
 * The big value display on the right shows the current selection,
 * comma-formatted (e.g. `1,500`) so 4-digit numbers stay readable.
 */
export function CommitmentStepper({
  value,
  onChange,
  unit = 'vehicle',
  snapPoints,
  labeledSnapPoints,
  quickPicks,
  title,
  description,
  className,
}: CommitmentStepperProps) {
  const { t } = useTranslation('m-billing');
  const isOrderUnit = unit === 'order';
  // `commitment_stepper` is keyed by unit so the copy can say "orders
  // per month" vs "vehicles" without the caller passing strings in.
  const copyKey = isOrderUnit ? 'order' : 'vehicle';

  const resolvedSnapPoints = useMemo(
    () => snapPoints ?? [...committedQtyConfigForUnit(unit).snapPoints],
    [snapPoints, unit],
  );

  const resolvedTitle = title ?? t(`commitment_stepper.${copyKey}.title`);
  const resolvedDescription = description ?? t(`commitment_stepper.${copyKey}.description`);
  const sliderMax = resolvedSnapPoints.length - 1;
  const plusGateway = resolvedSnapPoints[sliderMax];
  const labels = labeledSnapPoints ?? resolvedSnapPoints;
  const picks = quickPicks ?? resolvedSnapPoints;

  const isAtPlus = value >= plusGateway;

  const sliderIndex = useMemo(() => {
    if (isAtPlus) return sliderMax;
    return nearestIndex(resolvedSnapPoints, value);
  }, [resolvedSnapPoints, value, sliderMax, isAtPlus]);

  const handleSlide = (idx: number) => {
    if (idx === sliderMax) {
      // Landing on the plus stop preserves any custom value the
      // user has already typed; otherwise we seed it to the gateway.
      onChange(value > plusGateway ? value : plusGateway);
      return;
    }
    onChange(resolvedSnapPoints[idx] ?? resolvedSnapPoints[0]);
  };

  const Icon = isOrderUnit ? PackageIcon : TruckIcon;

  return (
    <div
      className={cn(
        'flex flex-col gap-5 rounded-2xl border border-gray-100 bg-white p-5 shadow-sm',
        className,
      )}>
      {/* Header row */}
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <div className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
            <Icon className="size-5" />
          </div>
          <div className="flex flex-col">
            <span className="text-sm font-semibold text-gray-900">{resolvedTitle}</span>
            <span className="text-xs text-gray-500">{resolvedDescription}</span>
          </div>
        </div>

        <div className="shrink-0 text-end">
          <div className="flex items-baseline justify-end gap-1.5">
            <span className="text-3xl font-bold tabular-nums text-gray-900">
              {formatNumberWithCommas(value)}
            </span>
            <span className="text-xs font-medium uppercase tracking-wide text-gray-500">
              {t(`commitment_stepper.${copyKey}.unit`)}
            </span>
          </div>
        </div>
      </div>

      {/* Quick-pick chips */}
      <div className="flex flex-wrap gap-1.5">
        {picks.map((pick, i) => {
          const isLast = i === picks.length - 1;
          const isActive = isLast ? isAtPlus : value === pick && !isAtPlus;
          return (
            <button
              key={pick}
              type="button"
              onClick={() => {
                if (isLast) {
                  onChange(value > pick ? value : pick);
                } else {
                  onChange(pick);
                }
              }}
              className={cn(
                'rounded-full border px-3 py-1 text-xs font-medium transition',
                isActive
                  ? 'border-primary bg-primary text-white shadow-sm'
                  : 'border-gray-200 bg-white text-gray-600 hover:border-primary/40 hover:text-primary',
              )}>
              {isLast ? `${pick}+` : pick}
            </button>
          );
        })}
      </div>

      {/* Slider */}
      <div className="flex flex-col gap-2">
        <input
          type="range"
          min={0}
          max={sliderMax}
          step={1}
          value={sliderIndex}
          onChange={e => handleSlide(Number(e.target.value))}
          aria-label={resolvedTitle}
          className={cn(
            'h-2 w-full cursor-pointer appearance-none rounded-full bg-gray-200 accent-primary',
            '[&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:size-5 [&::-webkit-slider-thumb]:rounded-full',
            '[&::-webkit-slider-thumb]:bg-primary [&::-webkit-slider-thumb]:shadow [&::-webkit-slider-thumb]:ring-4 [&::-webkit-slider-thumb]:ring-primary/15',
            '[&::-webkit-slider-thumb]:transition hover:[&::-webkit-slider-thumb]:ring-primary/25',
            '[&::-moz-range-thumb]:size-5 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:border-0 [&::-moz-range-thumb]:bg-primary',
          )}
        />

        {/* Tick labels */}
        <div className="relative h-4 select-none">
          {resolvedSnapPoints.map((stop, i) => {
            if (!labels.includes(stop)) return null;
            const pct = (i / sliderMax) * 100;
            const isLast = i === sliderMax;
            return (
              <span
                key={stop}
                className="absolute top-0 -translate-x-1/2 text-[11px] font-medium tabular-nums text-gray-400"
                style={{ insetInlineStart: `${pct}%` }}>
                {stop}
                {isLast ? '+' : ''}
              </span>
            );
          })}
        </div>
      </div>

      {/* Plus-mode custom input */}
      {isAtPlus && (
        <CustomQtyInput value={value} min={plusGateway} copyKey={copyKey} onChange={onChange} />
      )}
    </div>
  );
}

interface CustomQtyInputProps {
  value: number;
  min: number;
  copyKey: 'vehicle' | 'order';
  onChange: (next: number) => void;
}

/**
 * Free-form quantity input shown when the slider sits on the "plus"
 * gateway.
 *
 * The UX is held together by three details:
 *
 * 1. **Local string draft.** We hold the typed text in component state
 *    instead of mirroring `value` directly, so the user can type any
 *    sequence of digits (including ones below `min`) without the parent
 *    snapping the input back. Without this, going from `100` to `150`
 *    forces the user to type past the minimum first (e.g. `1000` then
 *    delete) — which is the exact bug we're fixing here.
 * 2. **Select-all on focus.** Clicking the field selects the current
 *    text so the very next keystroke replaces it. Pair that with #1
 *    and typing `150` is just three keys: `1`, `5`, `0`.
 * 3. **Clamp + format on blur.** Anything below `min` (or empty / NaN)
 *    snaps to `min` when the field loses focus, and we re-render with
 *    `formatNumberWithCommas` so `1500` displays as `1,500` once the
 *    user is done. While focused we show raw digits so editing stays
 *    predictable.
 */
function CustomQtyInput({ value, min, copyKey, onChange }: CustomQtyInputProps) {
  const { t } = useTranslation('m-billing');
  const [isFocused, setIsFocused] = useState(false);
  const [draft, setDraft] = useState<string>(() => String(value));

  // Sync the draft when the parent value changes from outside (e.g. the
  // user nudges the slider back to the gateway). We only sync while the
  // field is unfocused — fighting the user mid-keystroke is exactly the
  // bug this component exists to avoid.
  useEffect(() => {
    if (isFocused) return;
    setDraft(String(value));
  }, [value, isFocused]);

  const displayValue = useMemo(() => {
    if (isFocused) return draft;
    const parsed = Number.parseInt(draft, 10);
    return Number.isFinite(parsed) ? formatNumberWithCommas(parsed) : draft;
  }, [draft, isFocused]);

  return (
    <div className="flex flex-col gap-1.5 rounded-xl border border-primary/20 bg-primary/[0.04] p-3">
      <label
        htmlFor="commitment-stepper-custom"
        className="text-[11px] font-semibold uppercase tracking-wide text-primary/80">
        {t(`commitment_stepper.${copyKey}.custom_label`)}
      </label>
      <div className="flex items-center gap-2">
        <input
          id="commitment-stepper-custom"
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          autoComplete="off"
          value={displayValue}
          onFocus={e => {
            setIsFocused(true);
            // Defer select() so the focus event finishes first; calling
            // select() synchronously inside onFocus is racy on some
            // browsers and can leave the cursor at the end instead.
            const el = e.currentTarget;
            requestAnimationFrame(() => el.select());
          }}
          onChange={e => {
            const sanitized = e.target.value.replace(/[^\d]/g, '');
            setDraft(sanitized);
            const parsed = Number.parseInt(sanitized, 10);
            if (Number.isFinite(parsed) && parsed >= min) {
              onChange(parsed);
            }
          }}
          onBlur={() => {
            setIsFocused(false);
            const parsed = Number.parseInt(draft, 10);
            const clamped = Number.isFinite(parsed) && parsed >= min ? parsed : min;
            setDraft(String(clamped));
            if (clamped !== value) onChange(clamped);
          }}
          className="w-32 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-sm font-medium tabular-nums text-gray-900 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/15"
        />
        <span className="text-xs text-gray-500">
          {t(`commitment_stepper.${copyKey}.unit_total`)}
        </span>
      </div>
      <span className="text-[11px] text-gray-500">
        {t(`commitment_stepper.${copyKey}.custom_hint`, { min })}
      </span>
    </div>
  );
}

function nearestIndex(snapPoints: number[], value: number): number {
  if (snapPoints.length === 0) return 0;
  let bestIndex = 0;
  let bestDiff = Math.abs(snapPoints[0] - value);
  for (let i = 1; i < snapPoints.length; i++) {
    const diff = Math.abs(snapPoints[i] - value);
    if (diff < bestDiff) {
      bestIndex = i;
      bestDiff = diff;
    }
  }
  return bestIndex;
}
